
import type { EffectManifest } from "./particles";
import { MODE_SPHERICAL } from "./particles";

export const REFINE_GLOW_MIN = 7;
export const REFINE_GLOW_MAX = 9;

export type RefineGlowFamily = "sword" | "bow" | "fanbell" | "smallsword";

/** The family `__GetRefinedEffect`'s `default:` branch picks
 *  (`InstanceBase.cpp:2711`): sword, two-handed and mount spear. */
export const DEFAULT_REFINE_GLOW_FAMILY: RefineGlowFamily = "sword";

export function refineGlowEffectFor(
  refine: number,
  family: RefineGlowFamily = DEFAULT_REFINE_GLOW_FAMILY,
): string | null {
  const level = Math.floor(refine);
  if (level < REFINE_GLOW_MIN || level > REFINE_GLOW_MAX) return null;
  return `refine_${family}_${level}`;
}

/** `CInstanceBase` affect indices (`UserInterface/InstanceBase.h:68-115`), the
 *  bit positions of `EntityDelta.affect_flags`. */
export const AFFECT_POISON = 3;
export const AFFECT_SLOW = 4;
export const AFFECT_STUN = 5;
export const AFFECT_GEOMGYEONG = 15;
export const AFFECT_GYEONGGONG = 17;
export const AFFECT_KWAESOK = 24;
export const AFFECT_PABEOP = 32;

/** The weapon-bone loops, by affect index: Sword Aura's `EFFECT_WEAPON+1`
 *  on `equip_right_hand` (`playersettingmodule.py:357`,
 *  `__Warrior_SetGeomgyeongAffect`, `InstanceBaseEffect.cpp:830-845`). */
export const AFFECT_LOOP_EFFECTS: ReadonlyMap<number, string> = new Map([
  [AFFECT_GEOMGYEONG, "affect_sword_aura"],
]);

/** The two-handed body's copy of `AFFECT_LOOP_EFFECTS`: `EFFECT_WEAPON+2`,
 *  `geom_spear_loop.mse` (`playersettingmodule.py:358`), which
 *  `__Warrior_SetGeomgyeongAffect` attaches in `IsTwoHandMode`
 *  (`InstanceBaseEffect.cpp:841-842`). */
export const AFFECT_LOOP_EFFECTS_TWO_HAND: ReadonlyMap<number, string> = new Map([
  [AFFECT_GEOMGYEONG, "affect_spear_aura"],
]);

/** The loop an affect index wears on the weapon bone, or null. `twoHand` is
 *  `IsTwoHandMode`, a body in `MODE_TWOHAND_SWORD` (`ActorInstance.cpp:251-257`).
 *  `effects` is the manifest's effect table, so a mapped index whose asset a
 *  given pack did not import is a silent no-op rather than a draw of a missing key. */
export function affectLoopEffectFor(
  index: number,
  effects: Readonly<Record<string, unknown>>,
  twoHand = false,
): string | null {
  const key = (twoHand ? AFFECT_LOOP_EFFECTS_TWO_HAND : AFFECT_LOOP_EFFECTS).get(index);
  if (key === undefined) return null;
  return Object.hasOwn(effects, key) ? key : null;
}

/** The bone each registered effect this build spawns on a body hangs on, as
 *  `RegisterEffect`/`RegisterCacheEffect` name it; `__AttachEffect` hands the
 *  name to `AttachEffectByID` (`InstanceBaseEffect.cpp:1029-1066`). An effect
 *  missing here registers "" and rides the body's root. */
export const EFFECT_BONES: ReadonlyMap<string, string> = new Map([
  ["stun", "Bip01 Head"], // playersettingmodule.py:237
  ["crit", "Bip01 R Hand"], // playersettingmodule.py:238
  ["affect_poison", "Bip01"], // playersettingmodule.py:364
  ["affect_stun", "Bip01 Head"], // playersettingmodule.py:366
  ["affect_gwigeom", "Bip01 R Finger2"], // playersettingmodule.py:373
  ["affect_fire", "Bip01"], // playersettingmodule.py:381
  ["affect_gicheon", "Bip01 R Hand"], // playersettingmodule.py:382
  ["affect_jeungryeok", "Bip01 L Hand"], // playersettingmodule.py:383
  ["affect_pabeop", "Bip01 Head"], // playersettingmodule.py:384
]);

/** Every bone `EFFECT_BONES` names, in the order `effectBoneFollowId` numbers
 *  them; the creature pack publishes an `effect:<bone>` slot for each. */
export const EFFECT_ATTACH_BONES: readonly string[] = [
  "Bip01", "Bip01 Head", "Bip01 R Hand", "Bip01 L Hand", "Bip01 R Finger2",
];

/** The follow id one bone of one body rides, apart from the body's own id,
 *  its hair's and its `.msm` rows' (`hairEntityId`, `attachedEffectFollowId`).
 *  Entity ids stay under 2^24. */
export function effectBoneFollowId(entityId: number, bone: string): number {
  const slot = EFFECT_ATTACH_BONES.indexOf(bone);
  return 0x10000000 | ((slot & 0x0f) << 24) | (entityId & 0x00ffffff);
}

/** The follow id `key` spawned on `entityId` rides: its registered bone's, or
 *  the body's own where it registers none. */
export function effectFollowId(entityId: number, key: string): number {
  const bone = EFFECT_BONES.get(key);
  return bone === undefined ? entityId : effectBoneFollowId(entityId, bone);
}

/** `RegisterEffect(EFFECT_AFFECT+index, ...)` for every index that has a file
 *  (`playersettingmodule.py:364-384`); `__SetAffect` attaches it while the bit is
 *  set (`InstanceBaseEffect.cpp:938-958`). Indices 14 and 18 register nothing,
 *  so Berserk and Stealth wear no loop. Each rides the bone `EFFECT_BONES`
 *  gives it, or the body's root. */
export const AFFECT_EFFECTS: ReadonlyMap<number, string> = new Map([
  [AFFECT_POISON, "affect_poison"], // playersettingmodule.py:364
  [AFFECT_SLOW, "affect_slow"], // playersettingmodule.py:365
  [AFFECT_STUN, "affect_stun"], // playersettingmodule.py:366
  [6, "affect_dungeon_ready"], // playersettingmodule.py:367
  [16, "affect_cheongeun"], // playersettingmodule.py:371
  [AFFECT_GYEONGGONG, "affect_gyeonggong"], // playersettingmodule.py:372
  [19, "affect_gwigeom"], // playersettingmodule.py:373
  [20, "affect_gongpo"], // playersettingmodule.py:374
  [21, "affect_jumagap"], // playersettingmodule.py:375
  [22, "affect_hosin"], // playersettingmodule.py:376
  [23, "affect_boho"], // playersettingmodule.py:377
  [AFFECT_KWAESOK, "affect_kwaesok"], // playersettingmodule.py:378
  [25, "affect_heuksin"], // playersettingmodule.py:379
  [26, "affect_muyeong"], // playersettingmodule.py:380
  [28, "affect_fire"], // playersettingmodule.py:381
  [29, "affect_gicheon"], // playersettingmodule.py:382
  [30, "affect_jeungryeok"], // playersettingmodule.py:383
  [AFFECT_PABEOP, "affect_pabeop"], // playersettingmodule.py:384
]);

/** Whether `affect_flags` has affect index `index` set. Arithmetic, not `&`:
 *  the word is wider than JavaScript's 32-bit bitwise operators. */
export function hasAffect(flags: number, index: number): boolean {
  return Math.floor(flags / 2 ** index) % 2 === 1;
}

/** The root-followed loops a body under `flags` wears this frame. Feather Walk
 *  and Swiftness attach only while the body is moving, and Swiftness only when
 *  Feather Walk is not also on it (`__SetAffect`, `InstanceBaseEffect.cpp:905-910`;
 *  `StartWalking`/`EndWalking`, `InstanceBaseMovement.cpp:149-181`). */
export function affectLoopKeys(flags: number, moving: boolean): string[] {
  const keys: string[] = [];
  for (const [index, key] of AFFECT_EFFECTS) {
    if (!hasAffect(flags, index)) continue;
    if (index === AFFECT_GYEONGGONG && !moving) continue;
    if (index === AFFECT_KWAESOK && (!moving || hasAffect(flags, AFFECT_GYEONGGONG))) continue;
    keys.push(key);
  }
  return keys;
}

export interface StatusLoopField {
  has(key: string): boolean;
  spawn(key: string, options: {
    xMm: number; yMm: number; zMm: number; followId: number;
  }): unknown;
  stopFollowing(entityId: number, key: string): void;
  isFollowing(entityId: number, key: string): boolean;
  /** Whether the field tracks a body for `followId`; a bone's is seeded at
   *  the root and moved onto the bone by the body's next draw. */
  following(followId: number): boolean;
  follow(followId: number, xMm: number, yMm: number, zMm: number): void;
}

export class StatusLoops {
  /** Entity id -> the effect keys attached to it right now. */
  private readonly attached = new Map<number, Set<string>>();

  frame(
    field: StatusLoopField,
    entityId: number,
    keys: Iterable<string>,
    at: { xMm: number; yMm: number; zMm: number },
  ): void {
    const wanted = new Set<string>();
    for (const key of keys) {
      if (field.has(key)) wanted.add(key);
    }
    const live = this.attached.get(entityId);
    for (const key of wanted) {
      const followId = effectFollowId(entityId, key);
      if (field.isFollowing(followId, key)) continue;
      if (followId !== entityId && !field.following(followId)) field.follow(followId, at.xMm, at.yMm, at.zMm);
      field.spawn(key, { ...at, followId });
    }
    if (live !== undefined) {
      for (const key of live) {
        if (!wanted.has(key)) field.stopFollowing(effectFollowId(entityId, key), key);
      }
    }
    if (wanted.size === 0) this.attached.delete(entityId);
    else this.attached.set(entityId, wanted);
  }

  /** The keys attached to this character right now. */
  active(entityId: number): ReadonlySet<string> {
    return this.attached.get(entityId) ?? EMPTY_KEYS;
  }

  /** A new world, or a character who left: `ClearAffects`
   *  (`UserInterface/InstanceBaseEffect.cpp:700-716`) detaches all of them. */
  forget(entityId: number): void {
    this.attached.delete(entityId);
  }

  retainOnly(field: StatusLoopField, live: ReadonlySet<number>): void {
    for (const [entityId, keys] of [...this.attached]) {
      if (live.has(entityId)) continue;
      for (const key of keys) field.stopFollowing(effectFollowId(entityId, key), key);
      this.attached.delete(entityId);
    }
  }

  clear(): void {
    this.attached.clear();
  }
}

const EMPTY_KEYS: ReadonlySet<string> = new Set();

// ------------------------------------------------------------- the empires

export function empireBannerEffect(empire: number): string | null {
  if (empire === 1) return "empire_a";
  if (empire === 2) return "empire_b";
  if (empire === 3) return "empire_c";
  return null;
}

export function viewerSeesEmpireOf(character: {
  readonly sameEmpire: boolean;
  readonly isGameMaster?: boolean;
  readonly hidden?: boolean;
  readonly drawable?: boolean;
}, viewerIsGameMaster = false): boolean {
  if (character.drawable === false) return false;
  if (!viewerIsGameMaster) {
    if (character.sameEmpire) return false;
    if (character.hidden === true) return false;
  }
  return character.isGameMaster !== true;
}

export function effectKeyOf(mseName: string): string {
  const lower = mseName.toLowerCase().replace(/\.mse$/, "");
  const cut = Math.max(lower.lastIndexOf("/"), lower.lastIndexOf("\\"));
  return cut === -1 ? lower : lower.slice(cut + 1);
}

/** One effect the caller wants kept alive at a bone this frame. The caller
 *  resolves the bone's world transform (`creaturemesh.ts`'s `weaponBone` for
 *  the weapon, the body root for a whole-body buff) and hands over its position
 *  and facing; this renderer draws there and nowhere else. */
export interface AnchoredEffect {
  /** A manifest effect key -- `refineGlowEffectFor`/`affectLoopEffectFor`. */
  readonly effect: string;
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
  /** Radians CCW from east; orbits the sprite cloud so it reads as attached to
   *  a facing blade rather than a fixed compass ring. */
  readonly headingRad?: number;
  /** 1 is the imported size. Scales the cloud with the character. */
  readonly scale?: number;
}

/** How many billboards make one glow cloud. This build's own -- see the module
 *  docstring; enough to read as a shimmer, few enough that a hundred glowing
 *  weapons stay inside the shared particle budget. */
export const GLOW_BILLBOARDS = 6;

/** How fast the cloud orbits, in turns per second. This build's own. */
export const GLOW_SPIN_HZ = 0.5;

/** The sprite one glow draws with, resolved once from the imported `.mse`. */
interface GlowSprite {
  /** Atlas rectangle in 0..1: `[x, y, w, h]`. */
  readonly cell: Float32Array;
  readonly r: number;
  readonly g: number;
  readonly b: number;
  readonly a: number;
  readonly halfWMm: number;
  readonly halfHMm: number;
  /** The blade-tip offset up the local z the emitter emits at -- `sword_7.mse`
   *  states `MOVING_TYPE_DIRECT 0 0 70` (700 mm), so the stars sit at the tip
   *  rather than the hand. */
  readonly offsetZMm: number;
}

/** The largest value in a `[time, value]` curve, or `fallback` for an empty
 *  one. Colour and size are read at their peak so a glow shows the tier's
 *  brightest, most saturated instant rather than an arbitrary sample. */
function curvePeak(curve: readonly (readonly number[])[], fallback: number): number {
  let peak = -Infinity;
  for (const row of curve) {
    const v = row[row.length - 1];
    if (v !== undefined && v > peak) peak = v;
  }
  return peak === -Infinity ? fallback : peak;
}

export class AnchoredGlows {
  private readonly sprites = new Map<string, GlowSprite>();

  constructor(manifest: EffectManifest) {
    const atlasW = manifest.atlas.width;
    const atlasH = manifest.atlas.height;
    for (const [key, effect] of Object.entries(manifest.effects)) {
      // Prefer an additive emitter (`dstBlend === 2`, `D3DBLEND_ONE`): a glow
      // adds light, and an alpha-blended emitter (Sword Aura's faint base
      // wash) reads as nothing on this additive cloud. Fall back to the first
      // emitter so every effect resolves to something.
      const emitters = effect.emitters;
      if (emitters.length === 0) continue;
      const chosen = emitters.find((e) => e.dstBlend === 2) ?? emitters[0]!;
      const frame = chosen.frames[0];
      if (frame === undefined) continue;
      const rect = manifest.textures[frame];
      if (rect === undefined) continue;
      const position = chosen.positionMm[0];
      this.sprites.set(key, {
        cell: new Float32Array([
          rect[0]! / atlasW,
          rect[1]! / atlasH,
          rect[2]! / atlasW,
          rect[3]! / atlasH,
        ]),
        r: curvePeak(chosen.red, 1),
        g: curvePeak(chosen.green, 1),
        b: curvePeak(chosen.blue, 1),
        a: Math.min(1, curvePeak(chosen.alpha, 1)),
        halfWMm: curvePeak(chosen.halfWidthMm, 100),
        halfHMm: curvePeak(chosen.halfHeightMm, 100),
        // positionMm rows are `[time, x, y, z]`; the z is the blade offset.
        offsetZMm: position !== undefined && position.length >= 4 ? position[3]! : 0,
      });
    }
  }

  /** Whether an effect key has a resolved sprite -- i.e. can be drawn. */
  has(key: string): boolean {
    return this.sprites.has(key);
  }

  fill(
    out: Float32Array,
    first: number,
    active: Iterable<AnchoredEffect>,
    nowMs: number,
    capacity: number,
    write: (
      out: Float32Array,
      at: number,
      cx: number,
      cy: number,
      cz: number,
      halfW: number,
      halfH: number,
      rotationRad: number,
      r: number,
      g: number,
      b: number,
      a: number,
      cell: ArrayLike<number>,
      mode: number,
    ) => void,
  ): number {
    const tS = nowMs / 1000;
    const step = (2 * Math.PI) / GLOW_BILLBOARDS;
    let written = 0;
    for (const glow of active) {
      const sprite = this.sprites.get(glow.effect);
      if (sprite === undefined) continue;
      const scale = glow.scale ?? 1;
      const heading = glow.headingRad ?? 0;
      const cos = Math.cos(heading);
      const sin = Math.sin(heading);
      const ringR = sprite.halfWMm * 0.8 * scale;
      for (let k = 0; k < GLOW_BILLBOARDS; k += 1) {
        if (first + written >= capacity) return written;
        const phase = tS * GLOW_SPIN_HZ * 2 * Math.PI + k * step;
        // A shallow orbit around the blade, with a little vertical wobble so
        // the cloud breathes rather than spins as a rigid disc.
        const lx = Math.cos(phase) * ringR;
        const ly = Math.sin(phase * 0.7) * ringR * 0.4;
        const lz = sprite.offsetZMm * scale + Math.sin(phase * 1.3) * sprite.halfHMm * 0.3 * scale;
        const wx = glow.xMm + lx * cos - ly * sin;
        const wy = glow.yMm + lx * sin + ly * cos;
        const wz = glow.zMm + lz;
        // Twinkle: each sprite fades and swells on its own phase, so the cloud
        // sparkles the way `sword_7.mse`'s ten short-lived stars do.
        const twinkle = 0.35 + 0.65 * (0.5 + 0.5 * Math.sin(phase * 2));
        const size = sprite.halfWMm * scale * (0.4 + 0.6 * twinkle);
        const sizeH = sprite.halfHMm * scale * (0.4 + 0.6 * twinkle);
        write(
          out,
          first + written,
          wx,
          wy,
          wz,
          size,
          sizeH,
          phase,
          sprite.r,
          sprite.g,
          sprite.b,
          sprite.a * twinkle,
          sprite.cell,
          MODE_SPHERICAL,
        );
        written += 1;
      }
    }
    return written;
  }
}
